import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';

const AboutStatItem = ({ icon, value, label, delay }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 24 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay, ease: 'easeOut' }}
      style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', padding: '1.5rem 1rem' }}
    >
      <div style={{ color: '#F5C300', marginBottom: '0.6rem' }}>{icon}</div>
      <span style={{ fontFamily: '"Barlow Condensed", sans-serif', color: '#F5C300', fontWeight: 800, fontSize: 'clamp(1.8rem, 3.5vw, 2.5rem)', lineHeight: 1 }}>{value}</span>
      <span style={{ fontFamily: 'Inter, sans-serif', color: 'rgba(255,255,255,0.75)', fontSize: '0.8rem', marginTop: '0.4rem', letterSpacing: '0.05em', textTransform: 'uppercase' }}>{label}</span>
    </motion.div>
  );
};

const statsData = [
  {
    icon: <svg width="36" height="36" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2L3 7v5c0 5.25 3.75 10.15 9 11.25C17.25 22.15 21 17.25 21 12V7L12 2zm-2 13l-4-4 1.41-1.41L10 12.17l6.59-6.59L18 7l-8 8z" /></svg>,
    value: 'MTA', label: 'Assured Workshop',
  },
  {
    icon: <svg width="36" height="36" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="1.8"><circle cx="12" cy="12" r="3" /><circle cx="12" cy="12" r="8" /><path strokeLinecap="round" d="M12 2v2M12 20v2M2 12h2M20 12h2" /></svg>,
    value: '3D', label: 'Laser Wheel Alignment',
  },
  {
    icon: <svg width="36" height="36" fill="currentColor" viewBox="0 0 24 24"><path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z" /></svg>,
    value: 'Family', label: 'Owned and Operated',
  },
  {
    icon: <svg width="36" height="36" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="1.8"><rect x="2" y="7" width="18" height="11" rx="2" /><path strokeLinecap="round" d="M20 10h2v5h-2M7 11v3M10 11v3M13 11v3" /></svg>,
    value: 'FREE', label: 'Battery Check Included',
  },
];

const StatsStrip = () => (
  <section style={{ background: '#1A1A1A', padding: '3rem 1.5rem' }}>
    <div style={{ maxWidth: '1280px', margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem' }} className="about-stats-grid">
      <style>{`@media(min-width:768px){.about-stats-grid{grid-template-columns:repeat(4,1fr)!important;}}`}</style>
      {statsData.map((s, i) => (
        <AboutStatItem key={i} icon={s.icon} value={s.value} label={s.label} delay={i * 0.1} />
      ))}
    </div>
  </section>
);

const About = () => {
  const storyRef = useRef(null);
  const storyInView = useInView(storyRef, { once: true, margin: '-60px' });

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4 }}
      style={{ paddingTop: '80px' }}
    >
      {/* ── HERO ──────────────────────────────────────────────── */}
      <section style={{ background: '#1A1A1A', padding: 'clamp(3.5rem, 8vw, 6rem) 1.5rem' }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span style={{ fontFamily: 'Inter, sans-serif', color: '#F5C300', fontWeight: 600, fontSize: '0.8rem', letterSpacing: '0.2em', textTransform: 'uppercase' }}>
              North City Service and Tyre Centre
            </span>
            <h1 style={{ fontFamily: '"Barlow Condensed", sans-serif', fontWeight: 800, fontSize: 'clamp(3rem, 7vw, 5.5rem)', color: '#ffffff', margin: '0.3rem 0 1rem', lineHeight: 1, letterSpacing: '0.02em' }}>
              About <span style={{ color: '#F5C300' }}>Us</span>
            </h1>
            <div style={{ width: '60px', height: '4px', background: '#F5C300', marginBottom: '1.5rem' }} />
            <p style={{ fontFamily: 'Inter, sans-serif', color: 'rgba(255,255,255,0.7)', fontSize: '1.05rem', lineHeight: 1.7, maxWidth: '560px', margin: 0 }}>
              A family owned workshop in Porirua, built on honest service and a genuine commitment to quality workmanship.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ── STORY SECTION ─────────────────────────────────────── */}
      <section style={{ padding: 'clamp(3.5rem, 7vw, 5.5rem) 1.5rem', background: '#ffffff' }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
          <motion.div
            ref={storyRef}
            initial={{ opacity: 0, y: 40 }}
            animate={storyInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, ease: 'easeOut' }}
            style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 400px), 1fr))', gap: 'clamp(2rem, 5vw, 4rem)', alignItems: 'center' }}
          >
            {/* Story text */}
            <div>
              <span style={{ fontFamily: 'Inter, sans-serif', color: '#F5C300', fontWeight: 600, fontSize: '0.8rem', letterSpacing: '0.2em', textTransform: 'uppercase' }}>Our Story</span>
              <h2 style={{ fontFamily: '"Barlow Condensed", sans-serif', fontWeight: 800, fontSize: 'clamp(2.2rem, 4vw, 3.2rem)', color: '#1A1A1A', margin: '0.4rem 0 1.25rem', lineHeight: 1.1 }}>
                Porirua's Trusted Family Workshop
              </h2>
              <div style={{ width: '48px', height: '4px', background: '#F5C300', marginBottom: '1.5rem' }} />

              <p style={{ fontFamily: 'Inter, sans-serif', color: '#555', fontSize: '0.95rem', lineHeight: 1.85, margin: '0 0 1.1rem' }}>
                North City Service and Tyre Centre has been serving the Porirua community with honest, reliable automotive repairs and servicing. As a family owned and operated workshop, we take pride in treating every customer and every vehicle with the same care and attention we would give our own.
              </p>
              <p style={{ fontFamily: 'Inter, sans-serif', color: '#555', fontSize: '0.95rem', lineHeight: 1.85, margin: '0 0 1.1rem' }}>
                We are a fully accredited MTA Assured workshop, which means our work meets the highest standards in the New Zealand automotive industry. From a basic WOF through to complex engine repairs, ECU chiptuning and 3D laser wheel alignment, our qualified technicians have the skills and equipment to get the job done right.
              </p>
              <p style={{ fontFamily: 'Inter, sans-serif', color: '#555', fontSize: '0.95rem', lineHeight: 1.85, margin: '0 0 1.75rem' }}>
                We believe in straight talking, transparent service. We will always explain what your vehicle needs, show you why, and give you a clear quote before any work begins. No surprises, no upselling, just honest workmanship from a team that genuinely cares about keeping Porirua moving.
              </p>

              <Link
                to="/contact"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
                  background: '#F5C300', color: '#1A1A1A',
                  fontFamily: '"Barlow Condensed", sans-serif', fontWeight: 700,
                  fontSize: '0.95rem', letterSpacing: '0.08em',
                  padding: '0.75rem 1.75rem', borderRadius: '9999px', textDecoration: 'none',
                  transition: 'background 0.2s, transform 0.2s',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = '#e6b800'; e.currentTarget.style.transform = 'scale(1.03)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = '#F5C300'; e.currentTarget.style.transform = 'scale(1)'; }}
              >
                BOOK A SERVICE
                <svg width="15" height="15" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </Link>
            </div>

            {/* Workshop interior image */}
            <img
              src="/images/workshop-interior.webp"
              alt="North City Service and Tyre Centre workshop interior"
              style={{ width: '100%', borderRadius: '16px', minHeight: '420px', objectFit: 'cover', objectPosition: 'center', display: 'block' }}
            />
          </motion.div>
        </div>
      </section>

      {/* ── STATS STRIP ─────────────────────────────────────── */}
      <StatsStrip />

      {/* ── VALUES ──────────────────────────────────────────── */}
      <section style={{ padding: 'clamp(3rem, 6vw, 5rem) 1.5rem', background: '#ffffff' }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            style={{ textAlign: 'center', marginBottom: '3rem' }}
          >
            <span style={{ fontFamily: 'Inter, sans-serif', color: '#F5C300', fontWeight: 600, fontSize: '0.8rem', letterSpacing: '0.2em', textTransform: 'uppercase' }}>How We Operate</span>
            <h2 style={{ fontFamily: '"Barlow Condensed", sans-serif', fontWeight: 800, fontSize: 'clamp(2.2rem, 4.5vw, 3.5rem)', color: '#1A1A1A', margin: '0.3rem 0 0.75rem' }}>
              Our Values
            </h2>
            <div style={{ width: '48px', height: '4px', background: '#F5C300', margin: '0 auto' }} />
          </motion.div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 280px), 1fr))', gap: '1.5rem' }}>
            {[
              {
                icon: (
                  <svg width="44" height="44" fill="none" stroke="#F5C300" viewBox="0 0 24 24" strokeWidth="1.8">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                ),
                title: 'Honest Service',
                desc: 'We tell you exactly what your vehicle needs and why. Clear explanations, accurate quotes and no unnecessary work. Every time.',
              },
              {
                icon: (
                  <svg width="44" height="44" fill="none" stroke="#F5C300" viewBox="0 0 24 24" strokeWidth="1.8">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                ),
                title: 'Expert Technicians',
                desc: 'Our team are qualified, experienced and passionate about getting the job done right. We keep our skills and knowledge current through ongoing training.',
              },
              {
                icon: (
                  <svg width="44" height="44" fill="none" stroke="#F5C300" viewBox="0 0 24 24" strokeWidth="1.8">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                ),
                title: 'Latest Equipment',
                desc: 'We invest in professional grade diagnostic tools, 3D laser alignment systems and up to date software to ensure accurate, efficient repairs every time.',
              },
            ].map((val, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: i * 0.15 }}
                viewport={{ once: true }}
                style={{
                  background: '#F9F9F9',
                  padding: '2rem',
                  borderRadius: '12px',
                  borderTop: '4px solid #F5C300',
                }}
              >
                <div style={{ marginBottom: '1.1rem' }}>{val.icon}</div>
                <h3 style={{ fontFamily: '"Barlow Condensed", sans-serif', fontWeight: 700, fontSize: '1.5rem', color: '#1A1A1A', margin: '0 0 0.6rem', letterSpacing: '0.03em' }}>
                  {val.title}
                </h3>
                <p style={{ fontFamily: 'Inter, sans-serif', color: '#666', fontSize: '0.9rem', lineHeight: 1.75, margin: 0 }}>
                  {val.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA BANNER ──────────────────────────────────────── */}
      <section style={{ background: '#1A1A1A', padding: 'clamp(3rem, 6vw, 4.5rem) 1.5rem', textAlign: 'center' }}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <h2 style={{ fontFamily: '"Barlow Condensed", sans-serif', fontWeight: 800, fontSize: 'clamp(2.2rem, 4.5vw, 3.5rem)', color: '#ffffff', margin: '0 0 0.75rem' }}>
            Come and See Us in <span style={{ color: '#F5C300' }}>Porirua</span>
          </h2>
          <p style={{ fontFamily: 'Inter, sans-serif', color: 'rgba(255,255,255,0.65)', fontSize: '1rem', margin: '0 0 2rem' }}>
            We would love to look after your vehicle. Book online or give us a call.
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', justifyContent: 'center' }}>
            <Link
              to="/contact"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
                background: '#F5C300', color: '#1A1A1A',
                fontFamily: '"Barlow Condensed", sans-serif', fontWeight: 700,
                fontSize: '1rem', letterSpacing: '0.08em',
                padding: '0.8rem 2.25rem', borderRadius: '9999px', textDecoration: 'none',
                transition: 'background 0.2s, transform 0.2s',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = '#e6b800'; e.currentTarget.style.transform = 'scale(1.03)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = '#F5C300'; e.currentTarget.style.transform = 'scale(1)'; }}
            >
              BOOK NOW
              <svg width="15" height="15" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/services"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
                background: 'transparent', color: '#ffffff',
                fontFamily: '"Barlow Condensed", sans-serif', fontWeight: 700,
                fontSize: '1rem', letterSpacing: '0.08em',
                padding: '0.8rem 2.25rem', borderRadius: '9999px', textDecoration: 'none',
                border: '2px solid rgba(255,255,255,0.5)',
                transition: 'border-color 0.2s, background 0.2s',
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = '#ffffff'; e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.5)'; e.currentTarget.style.background = 'transparent'; }}
            >
              VIEW SERVICES
            </Link>
          </div>
        </motion.div>
      </section>
    </motion.div>
  );
};

export default About;
